<template>
  <Drawer v-if="loggedIn && !fullScreenEditing" v-model:visible="sidebarVisible">
    <template #container="{ closeCallback }">
      <div class="flex flex-col h-full">
        <div class="flex items-center justify-between px-4 pt-4 shrink-0">
          <img :src="propsStore.logo" class="p-0 w-60" alt="" />
          <IconButton icon="times" rounded outlined small severity="danger" @click="closeCallback"></IconButton>
        </div>
        <div class="overflow-y-auto mt-4">
          <ul class="list-none px-4 m-0">
            <li v-for="item in sidebarItems">
              <a
                v-if="item.visible()"
                :id="item.label"
                class="group flex items-center cursor-pointer px-3 h-12 rounded-md text-surface-700 dark:text-surface-0/80 hover:bg-surface-100 dark:hover:bg-surface-700"
                @click="item.click"
              >
                <i :class="item.icon" class="text-2xl! text-primary-500! group-hover:scale-120 duration-100 ease-out"></i>
                <span class="pl-3 font-medium group-hover:scale-120 duration-100 ease-out origin-left">{{ item.label }}</span>
              </a>
            </li>
          </ul>
        </div>
        <div class="mt-auto m-5 flex flex-col text-sm">
          <p>Version: [9.1.5]</p>
          <p>© 2025 TS Partners Inc., All Rights Reserved</p>
        </div>
      </div>
    </template>
  </Drawer>
  <Menubar class="bg-surface-200! dark:bg-surface-900! sticky top-0 z-50 pl-0 rounded-t-none">
    <template #start v-if="loggedIn">
      <div class="flex flex-row ml-3">
        <div v-if="!fullScreenEditing" class="flex flex-row">
          <IconButton id="btnSidebar" icon="bars" text rounded severity="surface" @click="toggleSidebar"></IconButton>
          <p id="pageName" class="font-bold my-auto text-xl dark:text-surface-0/80">{{ pageName + titleExtra }}</p>
        </div>
        <div v-else class="flex flex-row gap-3 ml-3 dark:text-surface-0/80">
          <i class="pi pi-pen-to-square text-2xl"></i>
          <p id="editingTitle" class="font-bold my-auto text-xl">{{ editingTitle + titleExtra }}</p>
        </div>

        <InputGroup v-if="projects" class="w-fit! my-auto mx-5">
          <i class="pi pi-search text-xl! text-primary-500! font-extrabold absolute mt-[0.3rem] ml-2 z-10"></i>

          <AutoComplete
            id="quickLaunch"
            name="projectLookup"
            icon
            v-model="projectLookupResult"
            class="w-[24rem]! h-10!"
            input-class="p-2! pl-10! w-full rounded-lg! rounded-r-none!"
            forceSelection
            placeholder="Project Quick Launch"
            optionLabel="projectName"
            :suggestions="filteredProjects"
            @complete="search"
          />
          <IconButton id="quickLaunchButton" icon="arrow-circle-right" :disabled="disableLaunch" @click="launchProject"> </IconButton>
        </InputGroup>
      </div>
    </template>
    <template #end>
      <div class="flex flex-row">
        <div v-if="showReport" class="h-10 my-auto">
          <ReportsListButton :context="'SINGLE_DRAW'" :projectId="projectId" :drawId="drawId"></ReportsListButton>
        </div>
        <IconButton icon="expand" text rounded severity="secondary" @click="toggleFullscreen"></IconButton>
        <IconButton icon="palette" text rounded severity="secondary" @click="toggleTheme"></IconButton>
        <Listbox
          v-show="toggleVal"
          v-model="selectedTheme"
          :options="themes"
          optionLabel="name"
          class="absolute right-16 top-12 z-10 w-35 min-w-0!"
          listStyle="max-height:500px"
          @change="selectTheme"
          @blur="toggleTheme"
          :pt="{
            listContainer: 'dark:bg-surface-800',
            list: 'py-0',
            item: ({ context }: any) => ({
              class: [
                'font-normal',
                'leading-none',
                'relative',
                'border-0',
                'rounded-none',
                'm-0',
                'py-3 px-8',
                'bg-surface-900',
                { 'text-surface-700 dark:text-white/80': !context.focused && !context.selected },
                { 'bg-surface-200 dark:bg-surface-600/60 text-surface-700 dark:text-white/80': context.focused && !context.selected },
                { 'bg-primary-100 dark:bg-primary-800 text-primary-700 dark:text-white/80': context.focused && context.selected },
                { 'bg-primary-50 dark:bg-primary-800 text-primary-700 dark:text-white/80': !context.focused && context.selected },
                { 'hover:bg-surface-100 dark:hover:bg-surface-600/80': !context.focused && !context.selected },
                {
                  'hover:text-surface-700 hover:bg-surface-100 dark:hover:text-white dark:hover:bg-surface-600/80':
                    context.focused && !context.selected,
                },
                'focus-visible:outline-hidden focus-visible:outline-offset-0 focus-visible:ring-3 focus-visible:ring-inset focus-visible:ring-primary-400/50 dark:focus-visible:ring-primary-300/50',
                'transition-shadow',
                'duration-200',
                'cursor-pointer',
                'overflow-hidden',
                'whitespace-nowrap',
              ],
            }),
          }"
        />
        <IconButton id="btnDarkMode" :icon="darkVal ? 'moon' : 'sun'" text rounded severity="secondary" @click="toggleDarkMode"></IconButton>
        <IconButton icon="phone" text rounded severity="secondary" @click="displayContactInfo"></IconButton>
      </div>

      <TSDialog label="Contact Us" v-model:visible="showContact" icon="phone" size="w-[500px]!">
        <template #content>
          <div class="flex flex-col gap-3">
            <h1 class="font-bold text-3xl">Accudraw Support</h1>
            <p class="text-xl flex"><span class="pi pi-envelope mr-2 my-auto"></span>accudraw@tspartnersinc.com</p>
            <p class="text-xl flex"><span class="pi pi-phone mr-2 my-auto"></span>610-768-1103</p>
          </div>
        </template>
      </TSDialog>
    </template>
  </Menubar>
</template>

<script setup lang="ts">
import { useAuthStore } from "@/stores/auth";
import { usePropsStore } from "@/stores/props";
import type { AdminProjectSummary, ProjectSummary } from "@/types/Projects";
import { useFullscreen } from "@vueuse/core";
import type { AutoCompleteCompleteEvent } from "primevue/autocomplete";
import { useConfirm } from "primevue/useconfirm";

const router = useRouter();

const confirm = useConfirm();

const authStore = useAuthStore();
const role = ref(authStore.getUserInfo().scope);

const showContact = ref(false);
const displayContactInfo = (): void => {
  showContact.value = true;
};

interface Props {
  pageName: string;
  loggedIn?: boolean;
  projects?: AdminProjectSummary[];
  fullScreenEditing?: boolean;
  editingTitle?: string;
  unsaved?: boolean;
  projectId?: string;
  drawId?: string;
  showReport?: boolean;
  titleExtra?: string;
  logoutOnly?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  loggedIn: true,
  fullScreenEditing: false,
  editingTitle: "",
  unsaved: false,
  showReport: false,
  titleExtra: "",
  logoutOnly: false,
});

const sidebarVisible = ref(false);

const propsStore = usePropsStore();

const darkVal = ref(propsStore.darkMode);
const toggleVal = ref(false);
const selectedTheme = ref();
const lastTheme = ref();
const themes = ref([
  { name: "Default", code: "default" },
  { name: "Frost", code: "frost" },
  { name: "Minty", code: "minty" },
  { name: "Shipwreck", code: "shipwreck" },
  { name: "Coral", code: "coral" },
  { name: "Deep Woods", code: "deep woods" },
  { name: "Steely", code: "steely" },
]);

const toggleSidebar = (): void => {
  sidebarVisible.value = !sidebarVisible.value;
};

const { isFullscreen, enter, exit, toggle } = useFullscreen();
isFullscreen.value = document.fullscreenElement != null;

const toggleFullscreen = (): void => {
  if (isFullscreen.value) exit();
  else enter();
};

const toggleDarkMode = (): void => {
  document.documentElement.classList.toggle("dark");
  darkVal.value = !darkVal.value;
  propsStore.darkMode = darkVal.value;
};

const toggleTheme = (): void => {
  toggleVal.value = !toggleVal.value;

  if (toggleVal.value) {
    let currentTheme = document.documentElement.getAttribute("data-theme");
    for (let i = 0; i < themes.value.length; i++) {
      const theme = themes.value[i];
      if (theme.code === currentTheme) {
        selectedTheme.value = theme;
        lastTheme.value = theme;
        break;
      }
    }
  }
};

const selectTheme = (): void => {
  //Hack to get around deselecting
  if (selectedTheme.value === null) selectedTheme.value = lastTheme.value;
  lastTheme.value = selectedTheme.value;
  document.documentElement.setAttribute("data-theme", selectedTheme.value.code);
  propsStore.theme = selectedTheme.value.code;
};

const confirmRequired = ["add-project", "subdivision-dashboard"];

const tryRoute = (viewName: string): void => {
  const currentRoute = router.currentRoute.value.name;
  let confirmExitWithoutSaving = false;
  if (viewName === currentRoute) return;
  for (let route of confirmRequired) {
    if (currentRoute === route) {
      confirmExitWithoutSaving = props.unsaved;
      break;
    }
  }

  if (!confirmExitWithoutSaving) {
    route(viewName);
  } else {
    confirmPopup(
      confirm,
      "Leave Without Saving?",
      "Leaving this page will not save your work, are you sure you want to leave?",
      () => route(viewName),
      () => {}
    );
  }
};

const route = async (viewName: string): Promise<void> => {
  if (viewName === "admin-login" || viewName === "portal-login") {
    if (viewName === "admin-login") {
      await axios
        .post("/projects/unlock-all?userOnly=true")
        .then((response) => {
          handleApiResponse(
            response,
            () => {},
            () => {
              error("Error", "An error occurred while deleting file");
            }
          );
        })
        .catch((e) => errorApi(e));
    }
    sidebarVisible.value = false;
    const authStore = useAuthStore();
    authStore.clearSession();
  }
  router.push({
    name: viewName,
  });
};

const sidebarItems = ref([
  {
    label: "Dashboard",
    icon: "pi pi-home",
    click: () => tryRoute("admin-dashboard"),
    visible: () => role.value === "ADMIN" && !props.logoutOnly,
  },
  {
    label: "Projects",
    icon: "pi pi-hammer",
    visible: () => role.value === "USER" && !props.logoutOnly,
    click: () => tryRoute("portal-project-list"),
  },
  {
    label: "Add Project",
    icon: "pi pi-plus",
    click: () => tryRoute("add-project"),
    visible: () => role.value === "ADMIN" && !props.logoutOnly,
  },
  {
    label: "Projects",
    icon: "pi pi-hammer",
    click: () => tryRoute("project-list"),
    visible: () => role.value === "ADMIN" && !props.logoutOnly,
  },
  {
    label: "Entities",
    icon: "pi pi-users",
    click: () => tryRoute("list-entities"),
    visible: () => role.value === "ADMIN" && !props.logoutOnly,
  },
  {
    label: "Ledgers",
    icon: "pi pi-dollar",
    click: () => tryRoute("ledgers"),
    visible: () => role.value === "ADMIN" && !props.logoutOnly,
  },
  {
    label: "Subdivisions",
    icon: "pi pi-sitemap",
    click: () => tryRoute("subdivision-list"),
    visible: () => role.value === "ADMIN" && !props.logoutOnly,
  },
  {
    label: "List Manager",
    icon: "pi pi-list",
    click: () => tryRoute("list-manager"),
    visible: () => role.value === "ADMIN" && !props.logoutOnly,
  },
  {
    label: "Users",
    icon: "pi pi-id-card",
    click: () => tryRoute("list-users"),
    visible: () => role.value === "ADMIN" && !props.logoutOnly,
  },
  {
    label: "Custom Field Manager",
    icon: "pi pi-table",
    click: () => tryRoute("custom-fields"),
    visible: () => role.value === "ADMIN" && !props.logoutOnly,
  },
  {
    label: "Report Customization",
    icon: "pi pi-sort-alpha-down",
    click: () => tryRoute("report-customization"),
    visible: () => role.value === "ADMIN" && !props.logoutOnly,
  },
  {
    label: "Application Settings",
    icon: "pi pi-cog",
    click: () => tryRoute("app-settings"),
    visible: () => role.value === "ADMIN" && !props.logoutOnly,
  },
  {
    label: "Account",
    icon: "pi pi-user",
    click: () => tryRoute(role.value === "USER" ? "portal-my-account" : "my-account"),
    visible: () => !props.logoutOnly,
  },
  {
    label: "Logout",
    icon: "pi pi-sign-out",
    click: () => tryRoute(role.value === "USER" ? "portal-login" : "admin-login"),
    visible: () => true,
  },
]);

const projectLookupResult = ref<ProjectSummary>();
const filteredProjects = ref<ProjectSummary[]>([]);

const disableLaunch = computed<boolean>(() => projectLookupResult.value == null || typeof projectLookupResult.value != "object");

const getProjectLabel = (project: ProjectSummary): string => {
  return project.projectName + "(" + project.projectCode + ")";
};

const search = (event: AutoCompleteCompleteEvent): void => {
  if (props.projects == null) return;

  const projectList: AdminProjectSummary[] = props.projects;
  setTimeout(() => {
    if (!event.query.trim().length) {
      filteredProjects.value = [...projectList];
    } else {
      const searchTerm = event.query.toLowerCase();
      filteredProjects.value = projectList.filter((project: AdminProjectSummary) => {
        const nameMatch = project.projectName.toLowerCase().includes(searchTerm);
        const codeMatch = project.projectCode.toLowerCase().includes(searchTerm);
        const borrowerMatch = project.borrower.toLowerCase().includes(searchTerm);
        return nameMatch || codeMatch || borrowerMatch;
      });
    }
  }, 250);
};

const launchProject = (): void => {
  if (projectLookupResult.value == null || typeof projectLookupResult.value != "object") return;

  router.push({
    name: "project-dashboard",
    params: {
      id: projectLookupResult.value.projectId,
    },
  });
};
</script>
